/**
 * 自分のゲームの広告の一覧（/money/ads・2026-10-22 money/self-serve-ads-dash）。
 *
 * WHY お金の枠（MoneyShell）の中か: 広告は作り手のアカウント単位で、クレジット（前払い残高）から払う = 前払い残高・利用料と同じ所。
 *   ゲームの画面（ProjectShell）に置くと「このゲームの残高から」と読める。ナビは口がある（フラグ on）ときだけ出す（MoneyShell の adsOn）。
 * 並び: 何かしてほしい物（断られた・クレジット切れ・作りかけ）を上に（selfAds.sortForList）。数字は 1 本ずつの画面（AdDetailPage）で見る。
 */
import { Link } from 'react-router-dom';
import { useResource } from '../../hooks';
import { useT } from '../../i18n';
import { money } from '../../money';
import { Empty, ErrorState, PageHeader, Spinner } from '../../components/ui';
import { CAMPAIGNS_PATH, SETTINGS_PATH, isOff } from '../../selfAdsApi';
import { ADS_NEW_PATH, adPath, adResumePath, canEditCreative, sortForList, statusTone, viewsFor, type Campaign, type SelfAdSettings } from '../../selfAds';

export function AdsListPage() {
  const { t } = useT();
  const settings = useResource<SelfAdSettings>(SETTINGS_PATH);
  const list = useResource<{ campaigns: Campaign[] }>(CAMPAIGNS_PATH, 60_000);

  if (isOff(settings.error) || isOff(list.error)) {
    return (
      <>
        <PageHeader title={t('ssa.title')} />
        <Empty title={t('ssa.off.title')} icon="soon">
          <p>{t('ssa.off.body')}</p>
        </Empty>
      </>
    );
  }

  const s = settings.data;
  const price = s ? money(s.priceCentsPerMille, 'usd') : null;
  const campaigns = list.data ? sortForList(list.data.campaigns) : null;

  return (
    <>
      <PageHeader title={t('ssa.title')} lead={price ? t('ssa.lead', { price }) : undefined}>
        {campaigns && campaigns.length > 0 && !s?.allStopped && (
          <Link to={ADS_NEW_PATH} className="btn btn-primary">
            {t('ssa.new')}
          </Link>
        )}
      </PageHeader>

      {s?.allStopped && (
        <p className="alert alert-warn" role="status">
          {t('ssa.allStopped')}
        </p>
      )}

      {list.loading && !list.data && <Spinner />}
      {list.error && <ErrorState error={list.error} what={t('ssa.title')} onRetry={list.reload} />}

      {campaigns && campaigns.length === 0 && (
        <Empty title={t('ssa.empty.title')}>
          <p>{t('ssa.empty.body', { price: price ?? money(100, 'usd') })}</p>
          {!s?.allStopped && (
            <Link to={ADS_NEW_PATH} className="btn btn-primary">
              {t('ssa.new')}
            </Link>
          )}
        </Empty>
      )}

      {campaigns && campaigns.length > 0 && (
        <ul className="ssa-list">
          {campaigns.map((c) => (
            <li key={c.id}>
              <Link to={c.status === 'draft' ? adResumePath(c.id) : adPath(c.id)} className="ssa-row">
                <span className="ssa-row-main">
                  <strong>{c.targetName}</strong>
                  <span className="small muted ssa-row-line">{c.tagline || t('ssa.noLine')}</span>
                </span>
                <span className="ssa-row-side small">
                  <StatusPill c={c} />
                  <span className="muted">
                    {t('ssa.row.perDay', { amount: money(c.dailyCapCents, 'usd'), views: viewsFor(c.dailyCapCents, c.priceCentsPerMille) })}
                  </span>
                  {c.spentCents > 0 && <span className="muted">{t('ssa.row.spent', { amount: money(c.spentCents, 'usd') })}</span>}
                  {/* 次に何をするかを行に書く（押す前に分かる） */}
                  {canEditCreative(c) && <span className="ssa-row-next">{t(c.status === 'draft' ? 'ssa.row.continue' : 'ssa.row.fix')}</span>}
                  {/* 行全体が押せる印（「直して出す」だけが押せるように見えた・見た目の確認 2026-10-22） */}
                  <span className="ssa-row-chev" aria-hidden="true">›</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

/** 状態の札（一覧・数字の画面で同じ） */
export function StatusPill({ c }: { c: Pick<Campaign, 'status' | 'pauseReason'> }) {
  const { t } = useT();
  const tone = statusTone(c);
  return <span className={`pill ${tone ? `pill-${tone}` : ''}`}>{t(`ssa.status.${c.status}`)}</span>;
}
